import React, { useState, useEffect } from "react";
import { Button } from "../ui/components/Button";
import { generateReport, getEffectiveDateInfo } from "../features/cfo/data/api/trialBalanceApi";
import { InlineExcelViewer, clearParsedReportCache } from "../features/cfo/presentation/components/InlineExcelViewer";
import {
  getStoredReportMonths,
  setStoredReportMonths,
  calculateDefaultMonths,
  getStoredReportInfo,
  setStoredReportInfo,
  clearStoredReportInfo,
  shouldClearReportCacheOnLoad,
} from "../features/cfo/utils/reportingMonth";

export default function CashFlowStatementPage() {
  const [startMonth, setStartMonth] = useState<string>("");
  const [endMonth, setEndMonth] = useState<string>("");
  const [isLoadingDates, setIsLoadingDates] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [reportId, setReportId] = useState<number | null>(null);
  const [reportName, setReportName] = useState<string>("");

  // Fetch effective date and set default months on mount
  useEffect(() => {
    const initializeMonths = async () => {
      try {
        if (shouldClearReportCacheOnLoad()) {
          clearStoredReportInfo();
          clearParsedReportCache();
        }
        // Check if we have stored months
        const storedMonths = getStoredReportMonths();
        if (storedMonths) {
          setStartMonth(storedMonths.startMonth);
          setEndMonth(storedMonths.endMonth);
          const storedReport = getStoredReportInfo();
          if (
            storedReport &&
            storedReport.startMonth === storedMonths.startMonth &&
            storedReport.endMonth === storedMonths.endMonth
          ) {
            setReportId(storedReport.reportId);
            setReportName(storedReport.reportName);
          }
          setIsLoadingDates(false);
          return;
        }

        // Fetch effective date from backend
        const effectiveDateInfo = await getEffectiveDateInfo();
        const { effective_year, effective_month } = effectiveDateInfo.data;

        // Calculate default months
        const defaultMonths = calculateDefaultMonths(effective_year, effective_month);
        setStartMonth(defaultMonths.startMonth);
        setEndMonth(defaultMonths.endMonth);
        const storedReport = getStoredReportInfo();
        if (
          storedReport &&
          storedReport.startMonth === defaultMonths.startMonth &&
          storedReport.endMonth === defaultMonths.endMonth
        ) {
          setReportId(storedReport.reportId);
          setReportName(storedReport.reportName);
        }
      } catch (err: any) {
        // Fallback to current date if API fails
        const now = new Date();
        const defaultMonths = calculateDefaultMonths(now.getFullYear(), now.getMonth() + 1);
        setStartMonth(defaultMonths.startMonth);
        setEndMonth(defaultMonths.endMonth);
        const storedReport = getStoredReportInfo();
        if (
          storedReport &&
          storedReport.startMonth === defaultMonths.startMonth &&
          storedReport.endMonth === defaultMonths.endMonth
        ) {
          setReportId(storedReport.reportId);
          setReportName(storedReport.reportName);
        }
        console.error("Failed to fetch effective date:", err);
      } finally {
        setIsLoadingDates(false);
      }
    };

    initializeMonths();
  }, []);

  const getFirstDayOfMonth = (monthStr: string): string => {
    return `${monthStr}-01`;
  };

  const getLastDayOfMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split("-").map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, "0")}`;
  };

  const handleApply = async (): Promise<void> => {
    setError("");

    if (!startMonth || !endMonth) {
      setError("Please select both start and end months.");
      return;
    }

    if (startMonth > endMonth) {
      setError("Start month must be before or equal to end month.");
      return;
    }

    setStoredReportMonths(startMonth, endMonth);
    setIsGenerating(true);
    try {
      const response = await generateReport({
        from_date: getFirstDayOfMonth(startMonth),
        to_date: getLastDayOfMonth(endMonth),
        group_by: "monthwise",
        save_report: true,
      });

      const newReportId = response.data.report_id;
      if (!newReportId) {
        throw new Error("Report ID not returned from server.");
      }

      const newReportName = response.data.report_name || "";
      setReportId(newReportId);
      setReportName(newReportName);
      setStoredReportInfo({
        startMonth,
        endMonth,
        reportId: newReportId,
        reportName: newReportName,
      });
    } catch (err: any) {
      setError(err.message || "Failed to generate report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const headerActions = (
    <>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          Start Month
        </label>
        <input
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          End Month
        </label>
        <input
          type="month"
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <Button
        variant="brand-primary"
        onClick={handleApply}
        disabled={isGenerating || isLoadingDates || !startMonth || !endMonth}
        size="medium"
        className="h-[35px]"
      >
        {isGenerating ? "Applying..." : "Apply"}
      </Button>
    </>
  );

  const emptyState = isLoadingDates
    ? "Loading..."
    : "Select start and end months and click Apply to view the report.";

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-6 py-6 overflow-auto">
      {error && (
        <div className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
          {error}
        </div>
      )}

      <div className="flex w-full grow shrink-0 basis-0 overflow-hidden rounded-md border border-neutral-border bg-default-background">
        <InlineExcelViewer
          reportId={reportId}
          reportName={reportName}
          targetSheet="CFS"
          headerActions={headerActions}
          showHeaderWhenEmpty
          fallbackTitle="Cash Flow Statement"
          isGenerating={isGenerating}
          emptyState={emptyState}
          showStatusBar={false}
        />
      </div>
    </div>
  );
}
